import { RetailerClientState } from '@state/reducers/retailer-client/RetailerClientReducer';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { ClientSubscription } from '@models/Opa';
import { ProductType } from '@pages/product-analyzer/Product';
import {
  constructCountryOption,
  getSubscriptionForOtherProducts,
  mapDefaultSubscribedCountry,
  mapSubscribedCountries,
  saveErrorLogForOnChange,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import { bindActionCreators } from 'redux';
import { useDispatch } from 'react-redux';
import { actionCreatorsInitialFilters, actionCreatorsRetailerClient } from '@state/Index';
import { InitialFiltersState } from '@state/reducers/initial-filter/InitialFiltersReducer';
import { Option, PageName, PageType } from '@utils/pageConfig/Types';
import { MainFilters } from '@services/FiltersTypes';
import { constructCountryOptionForSideMenu } from '@utils/FilterUtils';
import { getAndStoreSavedFilters } from '@utils/AppSubscriptionUtils';
import { MultibannerFeatureStates } from '@models/SavedViews';

export interface RestoreMultibannerRetailerViewProps {
  pagesConfig: PagesConfig;
  retailerClientState: RetailerClientState;
  initialFilterState: InitialFiltersState;
  appliedCountry: Option[];
  pageName: PageName;
  pageType: PageType;
  setFilters: (filters: MainFilters) => void;
  setSelectedCountries: (option: Option[]) => void;
  setAppliedCountries: (option: Option[]) => void;
  setDefaultFilters: (value: MainFilters[]) => void;
  featureStates?: MultibannerFeatureStates;
}

/**
 * Hooks to restore the saved views for when multibanner retailer client is changed to other than the default retailer client
 */
export const useRestoreMultibannerRetailerClient = (): {
  restoreMultibannerRetailerClient: (props: RestoreMultibannerRetailerViewProps) => void;
} => {
  const dispatch = useDispatch();

  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateInitialFilters } = bindActionCreators(actionCreatorsInitialFilters, dispatch);

  const getSavedFilters = async (currentClientId: number, pagesConfig: PagesConfig) => {
    await getAndStoreSavedFilters(ProductType.PerformanceAnalyzer, currentClientId, pagesConfig, pagesConfig.userId);
  };

  const restoreMultibannerRetailerClient = (props: RestoreMultibannerRetailerViewProps) => {
    const {
      pagesConfig,
      retailerClientState,
      initialFilterState,
      appliedCountry,
      pageName,
      pageType,
      setFilters,
      setAppliedCountries,
      setSelectedCountries,
      setDefaultFilters,
      featureStates,
    } = props;

    if (retailerClientState && initialFilterState) {
      //Get current subscription for performance analyzer page
      const currentSubscriptions: ClientSubscription[] = getSubscriptionForOtherProducts(
        ProductType.PerformanceAnalyzer,
        pagesConfig,
        retailerClientState.clientId,
      );

      const subscribedCountries = mapSubscribedCountries(currentSubscriptions);

      if (subscribedCountries.length === 0) {
        return;
      }

      const currentClientId = currentSubscriptions[0].retailer;

      //Get default selected country for current retailer client
      const defaultSelectedCountry = mapDefaultSubscribedCountry(currentSubscriptions, appliedCountry);

      const defaultCountryCode = defaultSelectedCountry.countryCode;

      //Get the initial filter data based on country
      const filtersDataBasedOnCountry = initialFilterState.initialFilters.find(
        (filter) => filter.country === defaultCountryCode,
      );

      if (!filtersDataBasedOnCountry) {
        saveErrorLogForOnChange(pagesConfig.clientCountries, pagesConfig.appliedFilters, pageName);
        return;
      }

      getSavedFilters(currentClientId, pagesConfig);

      // Get the current side menu filters selection from pagesConfig
      const pagesConfigAllFilters = [...pagesConfig.appliedFilters];

      //Update Retailer Client and Initial Filters Redux state
      updateRetailerClient(retailerClientState);
      updateInitialFilters(initialFilterState);

      //Update pages config
      pagesConfig.setClientCountries(retailerClientState.countries);
      pagesConfig.setClientId(currentClientId);

      //Update filters data and default filters for PA page
      setFilters(filtersDataBasedOnCountry);
      setDefaultFilters(initialFilterState.initialFilters);

      setSelectedCountries(constructCountryOption(defaultSelectedCountry));
      setAppliedCountries(constructCountryOption(defaultSelectedCountry));

      if (pageType === PageType.OPPORTUNITY_REVIEW) {
        //This is to prevent the carry over for the clearAll button to RO and CL page
        constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultSelectedCountry, [PageType.RETAILER_OVERVIEW]);

        // Set feature for current retailer client
        if (featureStates) {
          featureStates.setFeature(featureStates.feature);
        }
      } else {
        constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultSelectedCountry, [PageType.OPPORTUNITY_REVIEW]);
      }
    }
  };

  return { restoreMultibannerRetailerClient };
};
